import { useEffect, useRef, useState } from "react";

import Box from "@/components/Box";
import RichText from "@/components/RichText";
import useWindowSize from "@/hooks/use-window-size";

import ChevronComponent from "../tpo/Chevron";

function Button({ onClick, mr, text, direction }) {
  return (
    <Box
      as="button"
      display="flex"
      alignItems="center"
      onClick={onClick}
      cursor="pointer"
      mr={mr}
      textTransform="uppercase"
      fontSize={10}
      letterSpacing={2.8}
      fontFamily="gilroyBold"
      lineHeight="12px"
      textAlign="left"
    >
      {text}
      <Box as="span">
        <ChevronComponent fill="black" direction={direction} />
      </Box>
    </Box>
  );
}

export function CollapseableText({
  children,
  richText,
  fontSize,
  lineHeight,
  maxLines,
  fontFamily,
  containerProps,
  closedStateButtonText,
  openedStateButtonText
}) {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();

  const { width: viewportWidth } = useWindowSize();
  const viewportWidthOnLoad = useRef();

  if (viewportWidthOnLoad.current === undefined) {
    viewportWidthOnLoad.current = viewportWidth;
  }

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Apply clamp styles for measurement
    const prevStyle = el.getAttribute("style") || "";
    el.style.display = "-webkit-box";
    el.style.webkitBoxOrient = "vertical";
    el.style.webkitLineClamp = String(maxLines);
    el.style.overflow = "hidden";

    // Using a frame lets layout settle (fonts, images).
    let raf = requestAnimationFrame(() => {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    });

    // Observe future resizes (responsive fonts, wrapping, container width)
    const ro = new ResizeObserver(() => {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    });
    ro.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.setAttribute("style", prevStyle);
    };
  }, [children, maxLines, richText]);

  return (
    <Box {...containerProps}>
      <Box
        ref={ref}
        fontSize={fontSize}
        fontFamily={fontFamily}
        lineHeight={`${lineHeight}em`}
        whiteSpace={richText ? undefined : "pre-line"}
        overflow={isOpen ? "visible" : "hidden"}
        display={isOpen ? "block" : "-webkit-box"}
        style={{
          WebkitBoxOrient: isOpen ? undefined : "vertical",
          WebkitLineClamp: isOpen ? undefined : String(maxLines)
        }}
        id="collapsible-text-content"
      >
        {richText ? (
          <RichText fontSize={fontSize} lineHeight={`${lineHeight}em`}>
            {children}
          </RichText>
        ) : (
          children
        )}
      </Box>
      {(isOverflowing || isOpen) && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            mr={2}
            onClick={() => setIsOpen(!isOpen)}
            // if your Button expects children instead of `text`, use {isOpen ? openedStateButtonText : closedStateButtonText}
            text={isOpen ? openedStateButtonText : closedStateButtonText}
            direction={isOpen ? "top" : "bottom"}
            aria-expanded={isOpen}
          />
        </Box>
      )}
    </Box>
  );
}

CollapseableText.defaultProps = {
  richText: false,
  lineHeight: 1.5,
  maxLines: 3,
  fontSize: 16,
  fontFamily: "gilroyMedium",
  closedStateButtonText: "read more",
  openedStateButtonText: "read less"
};

export function HeadlineCollapseableText({ lineHeight, fontSize, fontFamily, headLine, fullText }) {
  const [boolean, setBoolean] = useState(false);

  return (
    <Box>
      <Box
        lineHeight={`${lineHeight}em`}
        overflow="hidden"
        fontSize={fontSize}
        fontFamily={fontFamily}
        whiteSpace="pre-line"
      >
        {boolean ? fullText : headLine}
      </Box>

      <Box display="flex" justifyContent="flex-end" mt={4}>
        <Button
          mr={2}
          onClick={() => setBoolean(!boolean)}
          text={boolean ? "collapse" : "expand"}
          chevronDir={boolean ? "top" : "bottom"}
        />
      </Box>
    </Box>
  );
}

HeadlineCollapseableText.defaultProps = {
  lineHeight: 1.5,
  maxLines: 1,
  fontFamily: "gilroyMedium"
};
